import { Fragment } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { compose } from 'core/libs/recompose';
import themePropTypes from 'core/utils/prop-types/theme';
import { withRouter } from 'core/libs/router';
import axios from 'core/libs/axios';

import useRequest from 'core/hooks/useRequest';

import Image from 'core/components/Image';
import withBreakpoint from 'core/hocs/withBreakpoint';
import withTheme from 'core/hocs/withTheme';
import CommentsCount from 'core/components/CommentsCount';
import Button from 'core/components/Button';
import PageLoader from 'core/components/Loader/PageLoader';

// eslint-disable-next-line import/no-namespace
import * as SocialIcons from 'core/components/Icon/socials';

import { TOPIC_COMMENTS_ANCHOR, DISCUSS_BUTTON_TEXT } from 'core/site/constants';

import { dateFormat } from 'core/utils/dates';
import { capitalize } from 'core/utils/string-helper';
import { Element } from 'core/utils/scroller';

import styles from './index.styl';


const fontContract = '{ font }';

const renderCommentBranch = (comments, renderProps) => {
  const {
    theme,
    isMobile,
  } = renderProps;

  return (
    <ul className={cx(styles.comments)}>
      <style jsx>{`
        .comment
          font ${theme.controls.commentsPreview.comment.font}
        .date
          font ${theme.controls.commentsPreview.date.font}
          color ${theme.colors.hint}
        .name
          font ${theme.controls.commentsPreview.name.font}
      `}</style>
      {comments.map((comment) => {
        const SocialIcon = SocialIcons[capitalize(comment.sessionSourceIcon)];

        return (
          <li key={comment.id}>
            <div className={styles.item}>
              <div className={styles.avatar}>
                <div className={styles.avatarInner}>
                  <Image
                    url={comment.userpic}
                    maxWidth={isMobile ? 32 : 46}
                    aspectRatio={1}
                  />
                </div>
                {SocialIcon && (
                  <div className={cx(styles.social, styles[comment.sessionSourceIcon])}>
                    <SocialIcon />
                  </div>
                )}
              </div>
              <div className={styles.content}>
                <div className={styles.nameDate}>
                  <div className={'name'}>{comment.displayName}</div>
                  <div className={cx(styles.date, 'date')}>{dateFormat(comment.createdAt, 'dd.MM.yyyy HH:mm')}</div>
                </div>
                <div className={'comment'}>{comment.text}</div>
              </div>
            </div>
            {comment.children && renderCommentBranch(comment.children, renderProps)}
          </li>
        );
      })}
    </ul>
  );
};

function CommentsPreview(props, { ramblerCommentsAppId }) {
  const {
    commentsUrl,
    xid,
    limit,
    disableLoad,
    isMobile,
    theme,
    button: CustomButton,
    history,
  } = props;

  const {
    data: comments = [],
    isLoading,
  } = useRequest({
    queryKey: [
      'axios',
      'post',
      'https://comments.rambler.ru/api/v2/json/get_comments',
      {
        appId: ramblerCommentsAppId,
        xid,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    ],
    queryFn: ({ queryKey: [, method, url, params, headers] }) => axios[method](url, params, headers)
      .then(({ data }) => {
        const newData = data?.result?.slice(0, limit) || [];
        const commentMap = {};

        newData.forEach(comment => { commentMap[comment.id] = comment; });
        newData.forEach(comment => {
          if (comment.parentId) {
            const parent = commentMap[comment.parentId];
            parent.children = parent.children || [];
            parent.children.unshift(comment);
          }
        });

        return newData.filter(comment => {
          return !comment.parentId;
        });
      })
      .catch(() => []),
    enabled: process.env.BROWSER_RUNTIME && !!ramblerCommentsAppId && !disableLoad,
  });

  return (
    <div>
      <style jsx>{`
        .headline
          font ${theme.controls.commentsPreview.headline.font}
          color ${theme.controls.commentsPreview.headline.color}
          text-transform ${theme.controls.commentsPreview.headline.textTransform}

        .btn_wrapper
          margin ${theme.controls.commentsPreview.button.margin}
      `}</style>
      <Element name={TOPIC_COMMENTS_ANCHOR} />
      {isLoading && <PageLoader />}
      {!isLoading && comments.length > 0 && (
        <Fragment>
          <div className={'headline'}>{ DISCUSS_BUTTON_TEXT } (<CommentsCount xid={xid} disableIcon />)</div>
          {renderCommentBranch(comments, props)}
        </Fragment>
      )}
      <div className={comments.length > 0 && 'btn_wrapper'}>
        {CustomButton
          ? <CustomButton onClick={() => history.push(commentsUrl)} />
          : (
            <Button
              size={isMobile ? 'medium' : 'large'}
              onClick={() => history.push(commentsUrl)}
              minWidth='100%'
            >
              { DISCUSS_BUTTON_TEXT }
            </Button>
          )
        }
      </div>
    </div>
  );
}

CommentsPreview.propTypes = {
  /** @ignore */
  isMobile: PropTypes.bool,
  /** @ignore */
  theme: themePropTypes(`
    {
      fonts: {
        text
      },
      colors: {
        hint,
        primary
      },
      controls: {
        commentsPreview: {
          headline: {
            font,
            color,
            textTransform,
          },
          name: ${fontContract},
          date: ${fontContract},
          comment: ${fontContract},
          button: { margin }
        }
      },
    }
  `),

  /**
   * Ссылка на страницу коммантариев
   */
  commentsUrl: PropTypes.string.isRequired,

  /**
   * ID топика.
   *
   * Должен совпадать с ID используемым при инициализации Рамблер комментариев.
   */
  xid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  /**
   * Количество комментариев к выводу
   */
  limit: PropTypes.number,

  /**
   * Отключение загрузки комментариев
   */
  disableLoad: PropTypes.bool,

  /** @ignore */
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,

  /**
   * Можно переопределить кнопку "Обсудить" по умолчанию.
   */
  button: PropTypes.func,
};

CommentsPreview.defaultProps = {
  limit: 3,
};

CommentsPreview.contextTypes = {
  ramblerCommentsAppId: PropTypes.number.isRequired,
};


const CommentsPreviewWithHOCs = compose(
  withTheme,
  withRouter,
  withBreakpoint,
)(CommentsPreview);
CommentsPreviewWithHOCs.displayName = 'CommentsPreview';

export { CommentsPreview as StorybookComponent };
export default CommentsPreviewWithHOCs;
